import { Box, ButtonBase } from "@mui/material";
import type { JSX } from "react";

import { STATUS_FILTERS } from "./statusFilters.js";

/**
 * Status filter — a row of segmented **pills** (not a dropdown), the operator's
 * one-click switch between the status buckets All / New / Qualified / Rejected,
 * the **Today / This week / Last 7 days / This month / Last month** recency
 * views, and the **Closing soon** deadline view. Each pill
 * carries its live lead count; the active one is filled. Rendered as a
 * `role="tablist"` of `role="tab"` pills. Extracted from `LeadsPage.tsx`.
 */
export function StatusFilter({
  value,
  counts,
  onChange,
}: {
  value: string;
  counts: Record<string, number | undefined>;
  onChange: (key: string) => void;
}): JSX.Element {
  return (
    <Box
      role="tablist"
      aria-label="Lead status filter"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
    >
      {STATUS_FILTERS.map((t) => {
        const active = t.key === value;
        const count = counts[t.key];
        return (
          <ButtonBase
            key={t.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            sx={[
              { display: "inline-flex", alignItems: "center", gap: 0.625, borderRadius: 999, px: 1.5, py: 0.5, fontSize: 12, fontWeight: 500, transition: "color 150ms, background-color 150ms" },
              active
                ? { bgcolor: "primary.main", color: "primary.contrastText" }
                : { border: 1, borderColor: "divider", color: "text.disabled", "&:hover": { color: "text.primary" } },
            ]}
          >
            {t.label}
            {count !== undefined && (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  opacity: active ? 0.9 : 0.75,
                }}
              >
                {count.toLocaleString()}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
